import { InputLabel, Tooltip, CircularProgress } from '@mui/material';
import CustomModal from '../../Global/CustomModal';
import ThreeOrb from '../../Home/ThreeOrb';
import { ArrowRight, Info } from 'react-feather';
import { File } from '@phosphor-icons/react';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { getLectureUploadUrl } from '../../../services/aws';
import { enqueueSnackbar } from 'notistack';
import axios from 'axios';
import { hapticsImpactLight } from '../../../utils/haptics';
import { Capacitor } from '@capacitor/core';
import useWindowWidth from '../../../hooks/useWindowWidth';

interface LectureBulkUploadProps {
  handleClose: () => void;
  handleMapFile: (
    s3Key: string,
    originalFilename: string,
    existingLectureId?: string
  ) => void;
}

const ACCEPT_FILE_TYPES =
  '.pdf,.pptx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain';

const LectureBulkUpload: React.FC<LectureBulkUploadProps> = ({
  handleClose,
  handleMapFile,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const [lectureDescription, setLectureDescription] = useState<string>('');
  const [lectureDescriptionMode, setLectureDescriptionMode] = useState<
    'upload' | 'manual'
  >('upload');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const windowWidth = useWindowWidth();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleUploadAndMapFiles = async (files?: FileList | null) => {
    if (!files || files.length === 0) {
      console.error('No files were selected for upload');
      handleClose(); // Close if no files are actually passed
      return;
    }

    setIsUploading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      const s3Key = await uploadFile(file);
      if (s3Key) {
        handleMapFile(s3Key, file?.name || 'Pending Lecture');
      }
    });

    try {
      await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Error during bulk upload:', error);
      // Optionally, notify the user about the partial success or failure
      enqueueSnackbar('Some files failed to upload.', { variant: 'error' });
    } finally {
      setIsUploading(false);
      handleClose();
    }
  };

  const uploadFile = async (file?: File | null) => {
    if (!file) {
      console.error('No file was uploaded');
      return;
    }

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
    ];

    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar(
        `File ${file.name} has an unsupported type. Please upload a .pdf, .pptx, or .txt file.`,
        {
          autoHideDuration: 3000,
        }
      );
      return; // Return undefined or throw an error to be caught by Promise.all
    }
    // setIsUploading(true); // This will be handled by the calling function

    let uploadDetails;
    try {
      const fileInputField = document.getElementById('topic-input');
      if (fileInputField) {
        fileInputField.blur();
      }

      uploadDetails = await getLectureUploadUrl({
        originalFilename: file.name,
        contentType: file.type,
        fileSize: file.size,
      });

      if (!uploadDetails.uploadUrl || !uploadDetails.key) {
        throw new Error('Received invalid upload details from the server.');
      }
      if (file.size > uploadDetails.max_size_bytes) {
        enqueueSnackbar(
          `File ${file.name} exceeds the maximum allowed size of ${uploadDetails.max_size_mb}MB.`,
          { autoHideDuration: 3000 }
        );
        // throw new Error( // Throwing error will stop Promise.all, better to return and let it continue
        //   `File ${file.name} exceeds the maximum allowed size of ${uploadDetails.max_size_mb}MB.`
        // );
        return;
      }
    } catch (error) {
      console.error(`Failed to get upload URL for ${file.name}:`, error);
      // setIsUploading(false); // Handled by calling function
      return;
    }

    let s3Key: string;
    try {
      await axios.put(uploadDetails.uploadUrl, file, {
        headers: { 'Content-Type': file.type },
      });

      s3Key = uploadDetails.key;
      // setIsUploading(false); // Handled by calling function
    } catch (error) {
      console.error(`Failed to upload ${file.name} to S3:`, error);
      // setIsUploading(false); // Handled by calling function
      return;
    }
    return s3Key;
  };

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
    },
    []
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
    },
    []
  );

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleUploadAndMapFiles(files);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleUploadAndMapFiles(files);
    }
    if (event.target) {
      event.target.value = ''; // Reset file input
    }
  };

  const handleMapDescription = async () => {
    hapticsImpactLight();
    setIsUploading(true);

    // Convert description to a file and upload
    const textFile = new window.File(
      [lectureDescription],
      'lecture_content.txt',
      {
        type: 'text/plain',
      }
    );

    handleUploadAndMapFile(textFile);
  };

  const handleUploadAndMapFile = async (file?: File | null) => {
    const s3Key = await uploadFile(file);
    if (s3Key) {
      handleMapFile(s3Key, file?.name || 'Pending Lecture');
    }
    handleClose();
  };

  return (
    <CustomModal
      open={true}
      onClose={handleClose}
      name={'add-lecture modal--add-lecture--bulk'}
      glass={true}
    >
      <div className="modal_header">
        <h2>Upload One or More Lectures</h2>
      </div>
      <div className="modal_content">
        <form className="add-lecture-form" onSubmit={(e) => e.preventDefault()}>
          <div className={`add-lecture-form_content`}>
            <div className="add-lecture-form_content_input">
              <InputLabel htmlFor="topic-input" className="m-b-0-50">
                {lectureDescriptionMode === 'upload'
                  ? 'Lecture Slides or PDF'
                  : 'Lecture Content'}
                <Tooltip
                  title={
                    'Ora AI analyzes your lecture and matches it to the most relevant content.'
                  }
                  enterTouchDelay={0}
                >
                  <Info />
                </Tooltip>
              </InputLabel>
              {lectureDescriptionMode === 'upload' && (
                <DndContext sensors={sensors}>
                  <div
                    className={`upload-lecture ${isDragging ? 'upload-lecture--dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="upload-lecture_label">
                      <div className="upload-lecture_label_icon">
                        <File size={24} />
                      </div>
                      <div className="upload-lecture_label_text">
                        <div className="upload-lecture_label_text_main">
                          {Capacitor.isNativePlatform() || windowWidth <= 560
                            ? 'Tap to upload'
                            : 'Drag and drop or click to upload'}
                        </div>
                        <div className="upload-lecture_label_text_sub">
                          (PowerPoints or PDFs)
                        </div>
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="topic-input"
                      accept={ACCEPT_FILE_TYPES}
                      onChange={handleFileChange}
                      disabled={isUploading}
                      style={{ display: 'block', marginBottom: '0' }}
                      multiple // Allow multiple file selection
                    />
                    {isUploading && (
                      <div className="doc-upload-overlay">
                        <CircularProgress
                          sx={{
                            color: 'var(--type-body-dark)',
                            height: '1rem !important',
                            width: '1rem !important',
                          }}
                        />
                        Uploading Documents...
                      </div>
                    )}
                  </div>
                </DndContext>
              )}
              {lectureDescriptionMode === 'manual' && (
                <div className="manual-add">
                  <textarea
                    id="topic-input"
                    value={lectureDescription}
                    onChange={(e) => setLectureDescription(e.target.value)}
                    rows={4}
                    placeholder={'Paste or type lecture content here...'}
                    aria-labelledby="lecture-description-label"
                    maxLength={100000}
                  />
                  {!isUploading && lectureDescription.trim().length > 0 && (
                    <div className="map-button-container">
                      <button
                        type="button"
                        className="button"
                        onClick={handleMapDescription}
                        disabled={
                          isUploading || lectureDescription.trim().length === 0
                        }
                      >
                        Continue
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="add-lecture-form_content_input_footer">
              <div className="or">or</div>
              <button
                type="button"
                className="button button--link button--change-mode"
                onClick={() =>
                  setLectureDescriptionMode(
                    lectureDescriptionMode === 'upload' ? 'manual' : 'upload'
                  )
                }
              >
                {lectureDescriptionMode === 'upload'
                  ? 'Add Lecture Manually'
                  : 'Upload Lecture'}
                <ArrowRight />
              </button>
            </div>
          </div>
        </form>
      </div>
      <div
        className="ai-loader"
        style={{
          opacity: isUploading ? 1 : 0,
          pointerEvents: isUploading ? 'auto' : 'none',
        }}
      >
        <ThreeOrb isScreensaver={false} />
        <div className="ai-label">Uploading lectures...</div>
      </div>
    </CustomModal>
  );
};

export default LectureBulkUpload;
